import * as Types from '../model/types';
import * as Graph from '../graph';
import * as CGTypes from '../types';
import * as OpTypes from '../opTypes';
import * as Urls from '../_external/util/urls';

const reportArgTypes = {
  report: Types.maybe(Types.oneOrMany(Types.maybe('report'))),
};

const reportArgDescription = `A ${Graph.docType('report')}`;

const makeReportReturnType =
  (returnType: Types.Type) =>
  (inputs: CGTypes.OpInputNodes<typeof reportArgTypes>) =>
    OpTypes.mappableNullableTaggable(inputs.report.type, t => returnType);

const makeReportResolver =
  (applyFn: (report: any) => any) =>
  (inputs: CGTypes.OpResolverInputTypes<typeof reportArgTypes>) =>
    OpTypes.mappableNullableTaggableVal(inputs.report, v => applyFn(v));

export const opReportName = Graph.makeOp({
  hidden: true,
  name: 'report-name',
  argTypes: reportArgTypes,
  description: `Returns the name of the ${Graph.docType('report')}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The name of the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('string'),
  resolver: makeReportResolver(report => report.displayName),
});

export const opReportLink = Graph.makeOp({
  hidden: true,
  name: 'report-link',
  argTypes: reportArgTypes,
  description: `Returns the link to the ${Graph.docType('report')}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The link to the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('link'),
  resolver: makeReportResolver(report => ({
    name: report.displayName,
    url: Urls.reportView({
      entityName: report.project.entityName,
      projectName: report.project.name,
      reportID: report.id,
      reportName: report.displayName,
    }),
  })),
});

export const opReportDescription = Graph.makeOp({
  hidden: true,
  name: 'report-description',
  argTypes: reportArgTypes,
  description: `Returns the description of the ${Graph.docType('report')}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The description of the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('string'),
  resolver: makeReportResolver(report => report.description),
});

export const opReportCreatedAt = Graph.makeOp({
  hidden: true,
  name: 'report-createdAt',
  argTypes: reportArgTypes,
  description: `Returns the creation time of the ${Graph.docType('report')}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The creation time of the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('date'),
  resolver: makeReportResolver(report => new Date(report.createdAt + 'Z')),
});

export const opReportProject = Graph.makeOp({
  hidden: true,
  name: 'report-project',
  argTypes: reportArgTypes,
  description: `Returns the ${Graph.docType('project')} of the ${Graph.docType(
    'report'
  )}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The ${Graph.docType(
    'project'
  )} of the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('project'),
  resolver: makeReportResolver(report => report.project),
});

export const opReportCreator = Graph.makeOp({
  hidden: true,
  name: 'report-creator',
  argTypes: reportArgTypes,
  description: `Returns the ${Graph.docType(
    'user'
  )} who created the ${Graph.docType('report')}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The ${Graph.docType(
    'user'
  )} who created the ${Graph.docType('report')}`,
  returnType: makeReportReturnType('user'),
  resolver: makeReportResolver(report => report.user),
});

export const opReportViewCount = Graph.makeOp({
  hidden: true,
  name: 'report-viewcount',
  argTypes: reportArgTypes,
  description: `Returns the number of times the ${Graph.docType(
    'report'
  )} has been viewed`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The number of times the ${Graph.docType(
    'report'
  )} has been viewed`,
  returnType: makeReportReturnType('number'),
  resolver: makeReportResolver(report => report.viewCount),
});

export const opReportStargazers = Graph.makeOp({
  hidden: true,
  name: 'report-stargazers',
  argTypes: reportArgTypes,
  description: `Returns the number of users who starred the ${Graph.docType(
    'report'
  )}`,
  argDescriptions: {
    report: reportArgDescription,
  },
  returnValueDescription: `The number of users who starred the ${Graph.docType(
    'report'
  )}`,
  returnType: makeReportReturnType(Types.list('user')),
  resolver: makeReportResolver(report =>
    report.stargazers.edges.map((edge: any) => edge.node)
  ),
});
